import { useCallback, useEffect, useRef, useState } from 'react';

export interface CopyButtonProps {
  value: string;
  /** Shown on the button. Defaults to "Copiar". */
  label?: string;
  className?: string;
  title?: string;
}

/**
 * `navigator.clipboard` is unavailable on plain HTTP and in some embedded
 * browsers, so this falls back to the old selection trick rather than looking
 * like a dead button. Copying a URL is the very first thing this app asks
 * anyone to do; it failing silently would be the worst possible first
 * impression.
 */
async function copy(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Permission denied or an insecure context — fall through.
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
}

export function CopyButton({ value, label = 'Copiar', className, title }: CopyButtonProps) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);

  const onClick = useCallback(async () => {
    const copied = await copy(value);
    setState(copied ? 'copied' : 'failed');

    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), 2000);
  }, [value]);

  return (
    <button
      type="button"
      onClick={() => void onClick()}
      title={title ?? label}
      className={
        className ??
        'rounded-md border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs font-medium text-slate-200 transition hover:border-slate-600 hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400'
      }
    >
      {/* Announced politely so a screen reader hears the outcome, not just the label. */}
      <span aria-live="polite">
        {state === 'copied' ? '¡Copiado!' : state === 'failed' ? 'No se pudo copiar' : label}
      </span>
    </button>
  );
}
